import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { STATIC_ORGANIZATIONS, Organization } from '../../contexts/AuthContext';

const QuotaPage: React.FC = () => {
  const { darkMode } = useTheme();
  const [organizations, setOrganizations] = useState<Organization[]>(STATIC_ORGANIZATIONS);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentOrg, setCurrentOrg] = useState<Organization | null>(null);
  const [newQuota, setNewQuota] = useState<number>(0);
  
  // Calculate total quota and usage
  const totalQuota = organizations.reduce((sum, org) => sum + org.quota.pages, 0);
  const totalUsed = organizations.reduce((sum, org) => sum + org.quota.used, 0);
  const usagePercentage = Math.round((totalUsed / totalQuota) * 100);

  const handleEditQuota = (org: Organization) => {
    setCurrentOrg(org);
    setNewQuota(org.quota.pages);
    setIsEditModalOpen(true);
  };

  const handleSaveQuota = () => {
    if (!currentOrg || newQuota <= 0) return;
    
    const updatedOrgs = organizations.map(org => {
      if (org.id === currentOrg.id) {
        return {
          ...org,
          quota: {
            ...org.quota,
            pages: newQuota
          }
        };
      }
      return org;
    });
    
    setOrganizations(updatedOrgs);
    setIsEditModalOpen(false);
    setCurrentOrg(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Quota Management</h1>
      </div>

      {/* Summary Card */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
        <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Total Quota Usage</h2>
        <div className="flex items-center mb-2">
          <div className="text-2xl font-bold text-primary mr-4">{usagePercentage}%</div>
          <div className="flex-1">
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4">
              <div 
                className="bg-primary h-4 rounded-full" 
                style={{ width: `${usagePercentage}%` }}
              ></div>
            </div>
          </div>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {totalUsed.toLocaleString()} of {totalQuota.toLocaleString()} pages used across all organizations
        </p>
      </div>

      {/* Organizations Quota Table */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Organization Quotas</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Organization
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Quota
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Usage
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {organizations.map((org) => (
                <tr key={org.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{org.name}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">ID: {org.id}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-gray-100">{org.quota.pages.toLocaleString()} pages</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="text-sm text-gray-900 dark:text-gray-100 mr-2">
                        {Math.round((org.quota.used / org.quota.pages) * 100)}%
                      </div>
                      <div className="w-24 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${
                            org.quota.used / org.quota.pages > 0.9 ? 'bg-red-500' : 'bg-primary'
                          }`} 
                          style={{ width: `${(org.quota.used / org.quota.pages) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {org.quota.used.toLocaleString()} of {org.quota.pages.toLocaleString()} pages used
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button 
                      className="text-primary hover:text-blue-700 dark:hover:text-blue-400"
                      onClick={() => handleEditQuota(org)}
                    >
                      Edit Quota
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Quota Modal */}
      {isEditModalOpen && currentOrg && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              Edit Quota for {currentOrg.name}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label htmlFor="quota" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Page Quota
                </label>
                <input
                  type="number"
                  id="quota"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary dark:bg-gray-700 dark:text-white"
                  value={newQuota}
                  onChange={(e) => setNewQuota(parseInt(e.target.value) || 0)}
                  min={currentOrg.quota.used}
                />
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Current usage: {currentOrg.quota.used.toLocaleString()} pages
                </p>
              </div>
              
              <div className="mt-2">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-700 dark:text-gray-300">Usage</span>
                  <span className="text-gray-700 dark:text-gray-300">
                    {newQuota > 0 ? Math.round((currentOrg.quota.used / newQuota) * 100) : 0}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full ${
                      newQuota > 0 && currentOrg.quota.used / newQuota > 0.9 ? 'bg-red-500' : 'bg-primary'
                    }`}
                    style={{ width: `${newQuota > 0 ? (currentOrg.quota.used / newQuota) * 100 : 0}%` }}
                  ></div>
                </div>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary dark:focus:ring-offset-gray-800"
                onClick={() => setIsEditModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary dark:focus:ring-offset-gray-800"
                onClick={handleSaveQuota}
                disabled={newQuota < currentOrg.quota.used}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuotaPage;